import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiFetch } from '../client';
import type {
  NotificationResponse,
  PaginatedNotificationsResponse,
  UnreadCountResponse,
  MarkAllAsReadResponse,
  NotificationQueryParams,
} from '@pantry/shared';

/**
 * Query keys for notifications.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  pushTokens: () => [...notificationKeys.all, 'push-tokens'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
  list: (params?: NotificationQueryParams) => [...notificationKeys.all, 'list', params] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

/**
 * Push token data.
 */
export interface PushToken {
  id: string;
  userId: string;
  token: string;
  platform: 'ios' | 'android';
  deviceId: string | null;
  lastUsedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Notification preference data.
 *
 * Field naming convention: {notificationType}{Channel}
 * - Push: expiryRemindersPush, lowStockAlertsPush, sharingInvitationsPush
 * - Future: expiryRemindersEmail, lowStockAlertsEmail, etc.
 */
export interface NotificationPreference {
  id: string;
  userId: string;
  // Push notification preferences
  expiryRemindersPush: boolean | null;
  lowStockAlertsPush: boolean | null;
  sharingInvitationsPush: boolean | null;
  // Timing preferences
  reminderDays: number[] | null;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  timezone: string | null;
  notificationTime: string | null; // HH:MM format - when to receive daily notifications
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Data for registering a push token.
 */
export interface RegisterPushTokenData {
  token: string;
  platform: 'ios' | 'android';
  deviceId?: string;
}

/**
 * Data for updating notification preferences.
 */
export interface UpdatePreferencesData {
  // Push notification preferences
  expiryRemindersPush?: boolean;
  lowStockAlertsPush?: boolean;
  sharingInvitationsPush?: boolean;
  // Timing preferences
  reminderDays?: number[];
  quietHoursStart?: string | null;
  quietHoursEnd?: string | null;
  timezone?: string;
  notificationTime?: string; // HH:MM format
}

/**
 * Notification types supported by the system.
 */
export type NotificationType = 'expiry_reminder' | 'low_stock_alert' | 'sharing_invitation';

/**
 * Delivery channels for notifications.
 */
export type NotificationChannel = 'push' | 'email' | 'in_app';

// ==================== API Functions ====================

async function registerPushToken(data: RegisterPushTokenData): Promise<PushToken> {
  return apiFetch<PushToken>('/notifications/push-tokens', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

async function getPushTokens(): Promise<PushToken[]> {
  return apiFetch<PushToken[]>('/notifications/push-tokens');
}

async function removePushToken(token: string): Promise<void> {
  const encodedToken = encodeURIComponent(token);
  await apiFetch<void>(`/notifications/push-tokens/${encodedToken}`, {
    method: 'DELETE',
  });
}

async function getPreferences(): Promise<NotificationPreference> {
  return apiFetch<NotificationPreference>('/notifications/preferences');
}

async function updatePreferences(data: UpdatePreferencesData): Promise<NotificationPreference> {
  return apiFetch<NotificationPreference>('/notifications/preferences', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ==================== Notification Center API Functions ====================

async function getNotifications(
  params?: NotificationQueryParams
): Promise<PaginatedNotificationsResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.cursor) searchParams.set('cursor', params.cursor);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.isRead !== undefined) searchParams.set('isRead', String(params.isRead));

  const query = searchParams.toString();
  const url = query ? `/notifications?${query}` : '/notifications';
  return apiFetch<PaginatedNotificationsResponse>(url);
}

async function getUnreadCount(): Promise<UnreadCountResponse> {
  return apiFetch<UnreadCountResponse>('/notifications/unread-count');
}

async function markNotificationAsRead(notificationId: string): Promise<NotificationResponse> {
  return apiFetch<NotificationResponse>(`/notifications/${notificationId}/read`, {
    method: 'PATCH',
  });
}

async function markAllNotificationsAsRead(
  markAllCursor: string | null
): Promise<MarkAllAsReadResponse> {
  return apiFetch<MarkAllAsReadResponse>('/notifications/mark-all-read', {
    method: 'POST',
    body: JSON.stringify({ markAllCursor }),
  });
}

// ==================== Hooks ====================

/**
 * Hook to fetch the current user's push tokens.
 */
export function usePushTokens() {
  return useQuery({
    queryKey: notificationKeys.pushTokens(),
    queryFn: getPushTokens,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to register a push token.
 * Updates the cache on success.
 */
export function useRegisterPushToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: registerPushToken,
    onSuccess: (newToken) => {
      queryClient.setQueryData<PushToken[]>(notificationKeys.pushTokens(), (oldTokens) => {
        if (!oldTokens) return [newToken];
        // Replace existing token or add new one
        const filtered = oldTokens.filter((t) => t.token !== newToken.token);
        return [...filtered, newToken];
      });
    },
  });
}

/**
 * Hook to remove a push token.
 * Updates the cache on success.
 */
export function useRemovePushToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removePushToken,
    onSuccess: (_, removedToken) => {
      queryClient.setQueryData<PushToken[]>(notificationKeys.pushTokens(), (oldTokens) => {
        if (!oldTokens) return [];
        return oldTokens.filter((t) => t.token !== removedToken);
      });
    },
  });
}

/**
 * Hook to fetch notification preferences.
 */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: getPreferences,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to update notification preferences.
 * Updates the cache on success.
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePreferences,
    onSuccess: (data) => {
      queryClient.setQueryData(notificationKeys.preferences(), data);
    },
  });
}

// ==================== Notification Center Hooks ====================

/**
 * Hook to fetch paginated notifications with cursor-based pagination.
 * Uses infinite query for "load more" functionality.
 */
export function useNotificationList(params?: Omit<NotificationQueryParams, 'cursor'>) {
  return useInfiniteQuery({
    queryKey: notificationKeys.list(params),
    queryFn: ({ pageParam }) => getNotifications({ ...params, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30 * 1000, // 30 seconds - notifications should feel fresh
  });
}

/**
 * Hook to fetch unread notification count for badge display.
 * Polls frequently to keep badge up to date.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: getUnreadCount,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Poll every minute
  });
}

/**
 * Hook to mark a single notification as read.
 * Optimistically updates the cache.
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markNotificationAsRead,
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.list() });
      await queryClient.cancelQueries({ queryKey: notificationKeys.unreadCount() });

      // Snapshot previous values
      const previousLists = queryClient.getQueriesData({ queryKey: notificationKeys.list() });
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(
        notificationKeys.unreadCount()
      );

      // Optimistically update notification in all cached lists
      queryClient.setQueriesData<{
        pages: PaginatedNotificationsResponse[];
        pageParams: unknown[];
      }>({ queryKey: notificationKeys.list() }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((item) =>
              item.id === notificationId
                ? { ...item, isRead: true, readAt: new Date().toISOString() }
                : item
            ),
          })),
        };
      });

      // Optimistically decrement unread count
      if (previousCount && previousCount.count > 0) {
        queryClient.setQueryData<UnreadCountResponse>(notificationKeys.unreadCount(), {
          count: previousCount.count - 1,
        });
      }

      return { previousLists, previousCount };
    },
    onError: (_err, _notificationId, context) => {
      // Rollback on error
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousCount) {
        queryClient.setQueryData(notificationKeys.unreadCount(), context.previousCount);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
  });
}

/**
 * Hook to mark all notifications as read.
 * Uses the markAllCursor to prevent race conditions with new notifications.
 */
export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllNotificationsAsRead,
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.list() });
      await queryClient.cancelQueries({ queryKey: notificationKeys.unreadCount() });

      // Snapshot previous values
      const previousLists = queryClient.getQueriesData({ queryKey: notificationKeys.list() });
      const previousCount = queryClient.getQueryData<UnreadCountResponse>(
        notificationKeys.unreadCount()
      );

      // Optimistically mark all as read in cached lists
      queryClient.setQueriesData<{
        pages: PaginatedNotificationsResponse[];
        pageParams: unknown[];
      }>({ queryKey: notificationKeys.list() }, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((item) => ({
              ...item,
              isRead: true,
              readAt: item.readAt ?? new Date().toISOString(),
            })),
          })),
        };
      });

      // Optimistically set count to 0
      queryClient.setQueryData<UnreadCountResponse>(notificationKeys.unreadCount(), { count: 0 });

      return { previousLists, previousCount };
    },
    onError: (_err, _cursor, context) => {
      // Rollback on error
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousCount) {
        queryClient.setQueryData(notificationKeys.unreadCount(), context.previousCount);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
  });
}
